/**
 * Executive KPI Ticker - Board-Level Dynamic KPI Display
 * 
 * Features:
 * - Fixed first row with core KPIs (dynamic per year)
 * - Continuous scrolling ticker for operational drivers (stock market style)
 * - Smooth transitions, pause on hover
 * - Click to navigate to relevant section
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  DollarSign,
  Gem,
  Wallet,
  BarChart3,
  Factory,
  Truck,
  Settings,
  Target,
  AlertTriangle,
  Users,
  Leaf,
  Layers
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Colors matching the executive theme
const colors = {
  primary: '#0A192F',
  gold: '#C5A059',
  positive: '#10B981',
  negative: '#EF4444',
  warning: '#F59E0B',
  neutral: '#64748B',
};

// Core KPIs by year - Key operational & support metrics for board
const coreKPIsByYear = {
  2026: [
    { id: 'oreExtracted', label: { pt: 'Minério Extraído', en: 'Ore Extracted' }, value: 1.08, unit: 'Mt', vsBudget: -6.1, vsYoY: -4.2, icon: Truck, section: 'mining', path: '/operations' },
    { id: 'wasteRemoved', label: { pt: 'Estéril Removido', en: 'Waste Removed' }, value: 2.1, unit: 'Mt', vsBudget: -4.5, vsYoY: -2.3, icon: Truck, section: 'mining', path: '/operations' },
    { id: 'stockpile', label: { pt: 'Stockpile', en: 'Stockpile' }, value: 125, unit: 'Kt', vsBudget: 12.5, vsYoY: 8.3, icon: Layers, section: 'overview', path: '/operations' },
    { id: 'avgPriceRegular', label: { pt: 'Preço Médio Regulares/ct', en: 'Avg Price Regular/ct' }, value: 336, unit: 'USD', vsBudget: 12.0, vsYoY: 8.5, icon: DollarSign, section: 'revenue', path: '/finance' },
    { id: 'caratsProduced', label: { pt: 'Quilates Recuperados', en: 'Carats Recovered' }, value: 42.5, unit: 'K cts', vsBudget: -5.6, vsYoY: -2.1, icon: Gem, section: 'production', path: '/operations' },
  ],
  2025: [
    { id: 'oreExtracted', label: { pt: 'Minério Extraído', en: 'Ore Extracted' }, value: 4.32, unit: 'Mt', vsBudget: 1.2, vsYoY: 3.5, icon: Truck, section: 'mining', path: '/operations' },
    { id: 'wasteRemoved', label: { pt: 'Estéril Removido', en: 'Waste Removed' }, value: 8.4, unit: 'Mt', vsBudget: 2.1, vsYoY: 1.8, icon: Truck, section: 'mining', path: '/operations' },
    { id: 'stockpile', label: { pt: 'Stockpile', en: 'Stockpile' }, value: 112, unit: 'Kt', vsBudget: -3.5, vsYoY: -5.2, icon: Layers, section: 'overview', path: '/operations' },
    { id: 'avgPriceRegular', label: { pt: 'Preço Médio/ct', en: 'Avg Price/ct' }, value: 336, unit: 'USD', vsBudget: 12.0, vsYoY: 13.9, icon: DollarSign, section: 'revenue', path: '/finance' },
    { id: 'caratsProduced', label: { pt: 'Quilates Recuperados', en: 'Carats Recovered' }, value: 168.2, unit: 'K cts', vsBudget: -2.8, vsYoY: 1.5, icon: Gem, section: 'production', path: '/operations' },
  ],
};

// Indicadores de Suporte (ticker) - Operational & Financial support indicators for board context
const operationalDriversByYear = {
  2026: [
    { id: 'stockpileGrade', label: { pt: 'Teor Médio Stockpile', en: 'Avg Stockpile Grade' }, value: 14.5, unit: 'cpht', vsBudget: -3.3, vsYoY: -0.7, icon: Layers, section: 'stocks', path: '/operations' },
    { id: 'feedGrade', label: { pt: 'Teor de Alimentação', en: 'Feed Grade' }, value: 15.74, unit: 'cpht', vsBudget: -2.2, vsYoY: -1.0, icon: Factory, section: 'plant', path: '/operations' },
    { id: 'dmsConversion', label: { pt: 'Conversão para DMS', en: 'DMS Conversion' }, value: 82.5, unit: '%', vsBudget: -1.8, vsYoY: 2.3, icon: Settings, section: 'plant', path: '/operations' },
    { id: 'dmsVolume', label: { pt: 'Volume Total de DMS', en: 'Total DMS Volume' }, value: 892, unit: 'Kt', vsBudget: -4.2, vsYoY: -1.5, icon: Truck, section: 'plant', path: '/operations' },
    { id: 'nonProductiveVol', label: { pt: 'Volume Não Produtivo', en: 'Non-Productive Volume' }, value: 156, unit: 'Kt', vsBudget: 12.4, vsYoY: 8.2, icon: AlertTriangle, section: 'efficiency', path: '/operations', inverse: true },
    { id: 'plantDowntime', label: { pt: 'Horas Paragem Planta', en: 'Plant Downtime Hours' }, value: 142, unit: 'hrs', vsBudget: 18.5, vsYoY: 15.2, icon: Settings, section: 'efficiency', path: '/operations', inverse: true },
    { id: 'plantUtilization', label: { pt: 'Utilização Efetiva Planta', en: 'Effective Plant Utilization' }, value: 82.8, unit: '%', vsBudget: -4.8, vsYoY: -2.1, icon: Factory, section: 'efficiency', path: '/operations' },
    { id: 'specialPrice', label: { pt: 'Preço Médio Especiais/ct', en: 'Avg Specials Price/ct' }, value: 2850, unit: 'USD', vsBudget: 8.5, vsYoY: 12.3, icon: Gem, section: 'revenue', path: '/finance' },
    { id: 'netIncome', label: { pt: 'Resultado Líquido', en: 'Net Income' }, value: 18.2, unit: 'M USD', vsBudget: -12.5, vsYoY: -5.8, icon: DollarSign, section: 'income', path: '/finance' },
    { id: 'netDebtEbitda', label: { pt: 'Dívida Líq./EBITDA', en: 'Net Debt/EBITDA' }, value: 1.8, unit: 'x', vsBudget: -5.2, vsYoY: -8.5, icon: BarChart3, section: 'liquidity', path: '/finance' },
    { id: 'operatingCashFlow', label: { pt: 'Fluxo de Caixa Operacional', en: 'Operating Cash Flow' }, value: 28.5, unit: 'M USD', vsBudget: -8.2, vsYoY: 3.5, icon: Wallet, section: 'cashflow', path: '/finance' },
  ],
  2025: [
    { id: 'stockpileGrade', label: { pt: 'Teor Médio Stockpile', en: 'Avg Stockpile Grade' }, value: 14.8, unit: 'cpht', vsBudget: -2.6, vsYoY: 4.2, icon: Layers, section: 'stocks', path: '/operations' },
    { id: 'feedGrade', label: { pt: 'Teor de Alimentação', en: 'Feed Grade' }, value: 16.2, unit: 'cpht', vsBudget: 1.2, vsYoY: 3.5, icon: Factory, section: 'plant', path: '/operations' },
    { id: 'dmsConversion', label: { pt: 'Conversão para DMS', en: 'DMS Conversion' }, value: 84.2, unit: '%', vsBudget: 2.1, vsYoY: 5.8, icon: Settings, section: 'plant', path: '/operations' },
    { id: 'dmsVolume', label: { pt: 'Volume Total de DMS', en: 'Total DMS Volume' }, value: 3568, unit: 'Kt', vsBudget: -1.2, vsYoY: 2.8, icon: Truck, section: 'plant', path: '/operations' },
    { id: 'nonProductiveVol', label: { pt: 'Volume Não Produtivo', en: 'Non-Productive Volume' }, value: 485, unit: 'Kt', vsBudget: -5.2, vsYoY: -12.5, icon: AlertTriangle, section: 'efficiency', path: '/operations', inverse: true },
    { id: 'plantDowntime', label: { pt: 'Horas Paragem Planta', en: 'Plant Downtime Hours' }, value: 520, unit: 'hrs', vsBudget: -8.5, vsYoY: -15.2, icon: Settings, section: 'efficiency', path: '/operations', inverse: true },
    { id: 'plantUtilization', label: { pt: 'Utilização Efetiva Planta', en: 'Effective Plant Utilization' }, value: 86.5, unit: '%', vsBudget: 2.3, vsYoY: 4.8, icon: Factory, section: 'efficiency', path: '/operations' },
    { id: 'specialPrice', label: { pt: 'Preço Médio Especiais/ct', en: 'Avg Specials Price/ct' }, value: 2650, unit: 'USD', vsBudget: 5.2, vsYoY: 8.5, icon: Gem, section: 'revenue', path: '/finance' },
    { id: 'netIncome', label: { pt: 'Resultado Líquido', en: 'Net Income' }, value: 72.5, unit: 'M USD', vsBudget: -5.8, vsYoY: 12.3, icon: DollarSign, section: 'income', path: '/finance' },
    { id: 'netDebtEbitda', label: { pt: 'Dívida Líq./EBITDA', en: 'Net Debt/EBITDA' }, value: 1.5, unit: 'x', vsBudget: -12.5, vsYoY: -18.2, icon: BarChart3, section: 'liquidity', path: '/finance' },
    { id: 'operatingCashFlow', label: { pt: 'Fluxo de Caixa Operacional', en: 'Operating Cash Flow' }, value: 98.2, unit: 'M USD', vsBudget: 3.5, vsYoY: 15.8, icon: Wallet, section: 'cashflow', path: '/finance' },
  ],
};

// KPI Card Component for Core KPIs - Premium Compact Design
const CoreKPICard = ({ kpi, language, onClick }) => {
  const Icon = kpi.icon;
  const isBudgetPositive = kpi.inverse ? kpi.vsBudget < 0 : kpi.vsBudget > 0;
  const isBudgetNegative = kpi.inverse ? kpi.vsBudget > 0 : kpi.vsBudget < 0;
  const isYoYPositive = kpi.inverse ? kpi.vsYoY < 0 : kpi.vsYoY > 0;
  const isYoYNegative = kpi.inverse ? kpi.vsYoY > 0 : kpi.vsYoY < 0;
  
  const getBudgetColor = () => {
    if (kpi.vsBudget === null) return colors.neutral;
    if (isBudgetPositive) return colors.positive;
    if (isBudgetNegative) return colors.negative;
    return colors.neutral;
  };

  const getYoYColor = () => {
    if (kpi.vsYoY === null) return colors.neutral;
    if (isYoYPositive) return colors.positive;
    if (isYoYNegative) return colors.negative;
    return colors.neutral;
  };

  return (
    <div 
      onClick={onClick}
      className="group cursor-pointer transition-all duration-200 bg-white/5 hover:bg-white/10 rounded px-2.5 py-1.5 border border-white/10 hover:border-gold/30"
    >
      {/* Label Row */}
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon className="w-3 h-3 text-gold/80" />
        <span className="text-[9px] font-medium text-slate-400 uppercase tracking-wide">
          {kpi.label[language]}
        </span>
      </div>
      {/* Value + Badges Row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-baseline gap-1">
          <span className="text-sm font-bold font-mono text-white">
            {typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}
          </span>
          <span className="text-[9px] text-slate-500">{kpi.unit}</span>
        </div>
        <div className="flex items-center gap-0.5">
          {/* Budget Badge */}
          {kpi.vsBudget !== null && (
            <div 
              className="flex items-center text-[8px] font-bold px-1 py-0.5 rounded"
              style={{ backgroundColor: `${getBudgetColor()}20`, color: getBudgetColor() }}
            >
              <span>{kpi.vsBudget > 0 ? '+' : ''}{kpi.vsBudget}%</span>
              <span className="ml-0.5 opacity-70">Orç</span>
            </div>
          )}
          {/* YoY Badge */}
          {kpi.vsYoY !== null && (
            <div 
              className="flex items-center text-[8px] font-bold px-1 py-0.5 rounded"
              style={{ backgroundColor: `${getYoYColor()}20`, color: getYoYColor() }}
            >
              <span>{kpi.vsYoY > 0 ? '+' : ''}{kpi.vsYoY}%</span>
              <span className="ml-0.5 opacity-70">YoY</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Ticker Item Component for scrolling drivers
const TickerItem = ({ kpi, language, onClick, year }) => {
  const Icon = kpi.icon;
  const isPositive = kpi.inverse ? kpi.vsBudget < 0 : kpi.vsBudget > 0;
  const isNegative = kpi.inverse ? kpi.vsBudget > 0 : kpi.vsBudget < 0;
  const isYoYPositive = kpi.inverse ? kpi.vsYoY < 0 : kpi.vsYoY > 0;
  const isYoYNegative = kpi.inverse ? kpi.vsYoY > 0 : kpi.vsYoY < 0;
  
  const getTrendColor = (positive, negative) => {
    if (positive) return colors.positive;
    if (negative) return colors.negative;
    return colors.neutral;
  };

  const budgetLabel = year === 2026 ? 'vs Orç.' : 'vs Orç.';
  const yoyLabel = year === 2026 ? 'vs 1T25' : 'vs 2024';

  return (
    <div 
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-2.5 bg-white rounded-lg border border-slate-200 hover:border-gold/50 cursor-pointer transition-colors flex-shrink-0 mx-2"
      style={{ minWidth: '220px' }}
    >
      <div className="p-1.5 rounded bg-slate-100">
        <Icon className="w-3.5 h-3.5 text-slate-600" />
      </div>
      <div className="flex-1">
        <p className="text-[10px] font-medium text-slate-500 whitespace-nowrap">
          {kpi.label[language]}
        </p>
        <div className="flex items-baseline gap-1.5">
          <span className="text-sm font-bold font-mono text-primary">
            {typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}
          </span>
          <span className="text-[10px] text-slate-500">{kpi.unit}</span>
        </div>
      </div>
      <div className="flex flex-col items-end gap-0.5">
        {/* vs Budget */}
        {kpi.vsBudget !== null && (
          <div 
            className="flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap"
            style={{ backgroundColor: `${getTrendColor(isPositive, isNegative)}15`, color: getTrendColor(isPositive, isNegative) }}
          >
            {kpi.vsBudget > 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
            <span>{kpi.vsBudget > 0 ? '+' : ''}{kpi.vsBudget}% {budgetLabel}</span>
          </div>
        )}
        {/* vs YoY */}
        {kpi.vsYoY !== null && (
          <div 
            className="flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap"
            style={{ backgroundColor: `${getTrendColor(isYoYPositive, isYoYNegative)}15`, color: getTrendColor(isYoYPositive, isYoYNegative) }}
          >
            {kpi.vsYoY > 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
            <span>{kpi.vsYoY > 0 ? '+' : ''}{kpi.vsYoY}% {yoyLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const ExecutiveKPITicker = ({ period = '1T 2026', year = 2026, onYearChange }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const tickerRef = useRef(null);
  const animationRef = useRef(null);
  const scrollPosRef = useRef(0);

  const periodOptions = [
    { value: 2026, label: '1T 2026' },
    { value: 2025, label: '2025' },
  ];

  const coreKPIs = coreKPIsByYear[year] || coreKPIsByYear[2026];
  const operationalDrivers = operationalDriversByYear[year] || operationalDriversByYear[2026];

  // Continuous scrolling animation for ticker
  useEffect(() => {
    const ticker = tickerRef.current;
    if (!ticker) return;

    const scrollSpeed = 0.5; // pixels per frame
    
    const animate = () => {
      if (!isPaused && ticker) {
        scrollPosRef.current += scrollSpeed;
        
        // Reset position when first set of items has scrolled out
        const scrollWidth = ticker.scrollWidth / 2;
        if (scrollPosRef.current >= scrollWidth) {
          scrollPosRef.current = 0;
        }
        
        ticker.style.transform = `translateX(-${scrollPosRef.current}px)`;
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPaused, year]);

  const handleNavigate = useCallback((kpi) => {
    const url = kpi.section ? `${kpi.path}?section=${kpi.section}` : kpi.path;
    navigate(url);
  }, [navigate]);

  // Duplicate drivers for seamless loop
  const duplicatedDrivers = [...operationalDrivers, ...operationalDrivers];

  return (
    <div className="w-full">
      {/* First Row: Period + Core KPIs (Fixed) */}
      <div className="bg-primary border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-4 py-2">
          <div className="flex items-center gap-3">
            {/* Period Selector */}
            <div className="relative flex-shrink-0">
              <button 
                onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                className="flex items-center gap-2 bg-gold/20 hover:bg-gold/30 px-2.5 py-1.5 rounded border border-gold/30 hover:border-gold/50 transition-all duration-200 group"
                data-testid="period-selector-btn"
              >
                <div className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse" />
                <span className="text-white font-serif font-semibold text-xs whitespace-nowrap">{period}</span>
                <svg 
                  className={`w-3 h-3 text-gold/70 group-hover:text-gold transition-transform duration-200 ${showPeriodDropdown ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Dropdown */}
              {showPeriodDropdown && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50 min-w-[120px]">
                  {periodOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        if (onYearChange) onYearChange(option.value);
                        setShowPeriodDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors flex items-center justify-between ${
                        year === option.value ? 'text-primary font-semibold bg-gold/10' : 'text-slate-700'
                      }`}
                      data-testid={`period-option-${option.value}`}
                    >
                      <span>{option.label}</span>
                      {year === option.value && (
                        <svg className="w-4 h-4 text-gold" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="w-px h-6 bg-white/20 flex-shrink-0" />

            {/* Core KPIs */}
            <div className="flex-1 grid grid-cols-5 gap-2">
              {coreKPIs.map((kpi) => (
                <CoreKPICard 
                  key={kpi.id}
                  kpi={kpi}
                  language={language}
                  onClick={() => handleNavigate(kpi)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Second Row: Continuous Scrolling Ticker (Stock Market Style) */}
      <div 
        className="bg-slate-50 border-b border-slate-200 overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="max-w-[1600px] mx-auto px-2 py-2">
          <div className="flex items-center">
            {/* Drivers Label */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-lg mr-3 flex-shrink-0 z-10">
              <span className="text-[10px] font-bold text-primary uppercase tracking-wider">
                {language === 'pt' ? 'Indicadores de Suporte' : 'Support Indicators'}
              </span>
              {isPaused && (
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                  <span className="text-[9px] text-slate-500">{language === 'pt' ? 'Pausado' : 'Paused'}</span>
                </div>
              )}
            </div>

            {/* Scrolling Container */}
            <div className="flex-1 overflow-hidden">
              <div 
                ref={tickerRef}
                className="flex items-center"
                style={{ willChange: 'transform' }}
              >
                {duplicatedDrivers.map((kpi, idx) => (
                  <TickerItem 
                    key={`${kpi.id}-${idx}`}
                    kpi={kpi}
                    language={language}
                    year={year}
                    onClick={() => handleNavigate(kpi)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveKPITicker;
